import { getApiErrorMessage } from '@/shared/utils/apiResponse';
import {
  apiDelete,
  apiGet,
  apiPatch,
  apiPost,
  apiPut,
  downloadBlob,
  visualizarBlob,
} from '@/shared/utils/requestBuilder';
import type { DocumentoListItem, DocumentoTipo } from '@/types/processos/documentoStoreTypes';

export const documentosService = {
  listar: async (execucaoId: string) => {
    const response = await apiGet(`/processos/${execucaoId}/documentos`);
    if (!response.ok) throw new Error('Erro ao listar documentos');
    const body = await response.json();
    return body.data as DocumentoListItem[];
  },
  upload: async (
    execucaoId: string,
    file: File,
    dados?: Partial<
      Pick<DocumentoListItem, 'tipoDocumento' | 'descricao' | 'sigilo' | 'ordemExibicao'>
    >,
  ) => {
    const form = new FormData();
    form.append('execucaoId', execucaoId);
    if (dados?.tipoDocumento) form.append('tipoDocumento', dados.tipoDocumento as DocumentoTipo);
    if (dados?.descricao) form.append('descricao', dados.descricao);
    if (dados?.sigilo !== undefined) form.append('sigilo', String(dados.sigilo));
    if (dados?.ordemExibicao !== undefined)
      form.append('ordemExibicao', String(dados.ordemExibicao));
    form.append('file', file);

    const response = await apiPost(`/documentos/upload`, form);
    if (!response.ok) {
      const errorBody = await response.json();
      throw new Error(errorBody.error?.message || 'Erro ao fazer upload do documento');
    }
    const body = await response.json();
    return body.data as DocumentoListItem;
  },
  uploadParaProcesso: async (
    processoId: string,
    file: File,
    dados?: {
      tipoDocumento?: string;
      descricao?: string;
      sigilo?: boolean;
      interno?: boolean;
    },
  ) => {
    const form = new FormData();
    form.append('file', file);
    form.append('execucaoId', processoId);
    if (dados?.tipoDocumento) form.append('tipoDocumento', dados.tipoDocumento);
    if (dados?.descricao) form.append('descricao', dados.descricao);
    form.append('sigilo', dados?.sigilo ? 'true' : 'false');
    form.append('interno', dados?.interno ? 'true' : 'false');

    const response = await apiPost(`/processos/${processoId}/documentos/upload`, form);
    if (!response.ok) {
      // `getApiErrorMessage` em vez de unwrap à mão: o back responde
      // `{ success: false, error: { code, message } }`, e ler só o `message` de topo achatava toda
      // falha no fallback genérico. O helper compartilhado cobre também `{ error: 'texto' }`.
      const error = await response.json();
      throw new Error(getApiErrorMessage(error, 'Erro ao fazer upload do documento'));
    }
    const body = await response.json();
    return body.data;
  },
  atualizar: async (id: string, dados: Partial<DocumentoListItem>) => {
    const response = await apiPut(`/documentos/${id}`, dados);
    if (!response.ok) throw new Error('Erro ao atualizar documento');
    const body = await response.json();
    return body.data as DocumentoListItem;
  },
  remover: async (id: string) => {
    const response = await apiDelete(`/documentos/${id}`);
    if (!response.ok) throw new Error('Erro ao remover documento');
  },
  download: async (id: string, nomeArquivo: string) => {
    await downloadBlob(`/documentos/${id}/download`, nomeArquivo);
  },
  visualizar: async (id: string) => {
    await visualizarBlob(`/documentos/${id}/download`);
  },
  downloadUrl: async (id: string) => {
    const response = await apiGet(`/documentos/${id}/download`);
    if (!response.ok) throw new Error('Erro ao gerar URL de download');
    const body = await response.json();
    return body.data.url as string;
  },
  thumbnailUrl: async (id: string) => {
    const response = await apiGet(`/documentos/${id}/thumbnail`);
    if (!response.ok) throw new Error('Erro ao gerar URL da miniatura');
    const body = await response.json();
    return body.data.url as string;
  },
  substituir: async (id: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    const response = await apiPost(`/documentos/${id}/substituir`, form);
    if (!response.ok) throw new Error('Erro ao substituir documento');
    const body = await response.json();
    return body.data as { message: string };
  },
  patchOrdem: async (id: string, ordem: number) => {
    const response = await apiPatch(`/documentos/${id}/ordem`, { ordem });
    if (!response.ok) throw new Error('Erro ao atualizar ordem do documento');
    const body = await response.json();
    return body.data as DocumentoListItem;
  },
};
